import { useAppModal } from "@/contexts/AppModal/useAppModal";
import { useIndexDB } from "@/contexts/IndexDB/useIndexDB";
import useToast from "@/contexts/Toast/useToast";
import { Recording } from "@/models/recording";
import {
  Button,
  ButtonSeverity,
  ButtonVariant,
  DownloadIcon,
  HorizontalFlex,
  MediumTextM,
  Modal,
  ModalSize,
  NavigationLink,
  Space,
  VerticalFlex,
} from "@enxcs/dap-ui-stratos";
import { FC, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

const BASE_URL_IBABS = "https://www.ibabs.com";

export interface RecordingUploadInterruptedModalProps {
  recording: Recording;
  handleDelete: () => void;
  handleRetry: () => void;
}

const RecordingUploadInterruptedModal: FC<RecordingUploadInterruptedModalProps> = ({ recording, handleDelete, handleRetry }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const { t, i18n } = useTranslation();

  const { onCloseModal } = useAppModal();

  const { showErrorToast } = useToast();

  const { downloadAudioFromIndexedDB } = useIndexDB();

  const agendaDate = new Date(recording.date).toLocaleDateString("pt-PT");

  const handleDownloadAudio = async () => {
    try {
      setIsDownloading(true);
      await downloadAudioFromIndexedDB(recording.recordingId);
    } catch (error) {
      console.error("Error downloading audio from IndexedDB:", error);
      showErrorToast("failMessage.toast");
    } finally {
      setIsDownloading(false);
      onCloseModal();
    }
  };

  return (
    <Modal
      size={ModalSize.M}
      title={t("uploadInterruptedModal.title")}
      closeTitle={t("buttons.close")}
      body={
        <VerticalFlex $gap={Space.V8} $justify="space-between" $align={"flex-start"}>
          <MediumTextM>
            <Trans
              i18nKey="uploadInterruptedModal.message"
              values={{ agendaName: recording.title, agendaDate }}
              components={[
                <NavigationLink href={`${BASE_URL_IBABS}/${i18n.language}/contact/`} blank label={t("uploadInterruptedModal.linkLabel")}></NavigationLink>,
              ]}
            />
          </MediumTextM>
        </VerticalFlex>
      }
      footer={
        <HorizontalFlex $justify="space-between" $gap={Space.V8}>
          <Button variant={ButtonVariant.GHOST} severity={ButtonSeverity.DANGER} onClick={handleDelete} label={t("buttons.delete")} />
          <HorizontalFlex $justify="flex-end" $gap={Space.V8}>
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={handleDownloadAudio}
              label={t("buttons.downloadAudio")}
              rightIcon={DownloadIcon}
              disabled={isDownloading}
            />
            <Button variant={ButtonVariant.PRIMARY} label={t("buttons.tryAgain")} onClick={handleRetry} />
          </HorizontalFlex>
        </HorizontalFlex>
      }
      onClose={() => {
        onCloseModal();
      }}
    />
  );
};

export default RecordingUploadInterruptedModal;
